import React, { useState, useMemo, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { FiRefreshCw, FiUsers, FiCalendar, FiDownload, FiTrendingUp } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import Logo from '../common/Logo';
import { getGradeMasteryData, MasteryRecord, GradeMasteryRecord, GradeMasteryResponse } from '../../api/admin';
import { convertToCSV, downloadCSV, formatDateForFilename } from '../../utils/exportUtils';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  ChartLegend
);

// Helper function to convert Date to local YYYY-MM-DD string
const formatDateLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper function to format date for display (e.g., "November 4, 2024")
const formatDateDisplay = (date: Date): string => {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  };
  return date.toLocaleDateString('en-US', options);
};

// Access denied component for non-admin users
const AccessDenied = () => (
  <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex flex-col justify-center p-6">
    <div className="max-w-4xl mx-auto text-center">
      <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
      <p className="text-base text-gray-600 mt-2">You are not authorized to view this page.</p>
    </div>
  </div>
);

// Header component
const MasteryReportHeader = () => (
  <h1 className="text-3xl font-bold text-center pt-4 mb-4 flex items-center justify-center gap-3">
    <Logo size={28} />
    <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-transparent bg-clip-text">
      Grade Mastery Report
    </span>
  </h1>
);

// Action bar component
interface ActionBarProps {
  onRefresh: () => void;
  onDownloadCSV: () => void;
  isLoading?: boolean;
  hasData?: boolean;
  dateRange: string;
}

const ActionBar: React.FC<ActionBarProps> = ({
  onRefresh,
  onDownloadCSV,
  isLoading = false,
  hasData = false,
  dateRange
}) => {
  return (
    <Card className="shadow-lg border border-gray-100 mb-4">
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="flex items-center gap-2">
            <FiCalendar className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-600">Date Range: {dateRange}</span>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={onRefresh}
              disabled={isLoading}
              variant="outline"
              className="flex items-center px-4"
            >
              <FiRefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={onDownloadCSV}
              disabled={isLoading || !hasData}
              variant="outline"
              className="flex items-center px-4 bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
            >
              <FiDownload className="w-4 h-4 mr-2" />
              CSV
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Summary stats component
interface SummaryStatsProps {
  totalMasteries: number;
  totalGradeMasteries: number;
  session1Count: number;
  session2Count: number;
  isLoading?: boolean;
}

const SummaryStats: React.FC<SummaryStatsProps> = ({
  totalMasteries,
  totalGradeMasteries,
  session1Count,
  session2Count,
  isLoading = false
}) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {[...Array(3)].map((_, i) => (
          <Card key={i} className="shadow-lg border border-gray-100">
            <CardContent className="p-4">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-300 rounded mb-2"></div>
                <div className="h-8 bg-gray-300 rounded mb-2"></div>
                <div className="h-3 bg-gray-300 rounded w-2/3"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const stats = [
    {
      title: 'All Grades / Total Grades',
      value: `${totalMasteries.toLocaleString()} / ${totalGradeMasteries.toLocaleString()}`,
      icon: FiUsers,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      description: 'Students who mastered all grades / Total grade masteries'
    },
    {
      title: 'Total in Session 1',
      value: session1Count.toLocaleString(),
      icon: FiTrendingUp,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      description: 'Students who mastered up to Oct 3, 2025'
    },
    {
      title: 'Total in Session 2',
      value: session2Count.toLocaleString(),
      icon: FiTrendingUp,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
      description: 'Students who mastered from Oct 3, 2025 onwards'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      {stats.map((stat, index) => (
        <Card key={index} className="shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-sm text-gray-600 mb-1">{stat.title}</p>
                <p className={`text-2xl font-bold ${stat.color}`}>
                  {stat.value}
                </p>
              </div>
              <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">{stat.description}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// Mastery chart component
interface MasteryChartProps {
  data: {
    allGrades: Array<{ date: string; count: number }>;
    anyGrade: Array<{ date: string; count: number }>;
  };
  isLoading?: boolean;
}

const MasteryChart: React.FC<MasteryChartProps> = ({ data, isLoading = false }) => {
  if (isLoading) {
    return (
      <Card className="shadow-lg border border-gray-100 mb-6">
        <CardContent className="p-4">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-300 rounded w-1/3 mb-4"></div>
            <div className="h-64 bg-gray-300 rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (data.allGrades.length === 0 && data.anyGrade.length === 0) {
    return null;
  }

  // Get all unique dates from both datasets
  const allDates = new Set([
    ...data.allGrades.map(d => d.date),
    ...data.anyGrade.map(d => d.date)
  ]);
  const sortedDates = Array.from(allDates).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

  // Create maps for quick lookup
  const allGradesMap = new Map(data.allGrades.map(d => [d.date, d.count]));
  const anyGradeMap = new Map(data.anyGrade.map(d => [d.date, d.count]));

  // Prepare chart data for react-chartjs-2
  const chartData = {
    labels: sortedDates,
    datasets: [
      {
        label: 'All Grades Mastered (Users)',
        data: sortedDates.map(date => allGradesMap.get(date) || 0),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.1,
        yAxisID: 'y'
      },
      {
        label: 'Any Grade Mastered (Events)',
        data: sortedDates.map(date => anyGradeMap.get(date) || 0),
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        tension: 0.1,
        yAxisID: 'y1'
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        beginAtZero: true,
        ticks: {
          stepSize: 1
        },
        title: {
          display: true,
          text: 'All Grades Mastered (Users)'
        }
      },
      y1: {
        type: 'linear' as const,
        display: true,
        position: 'right' as const,
        beginAtZero: true,
        ticks: {
          stepSize: 1
        },
        title: {
          display: true,
          text: 'Any Grade Mastered (Events)'
        },
        grid: {
          drawOnChartArea: false,
        }
      },
      x: {
        title: {
          display: true,
          text: 'Date'
        }
      }
    }
  };

  return (
    <Card className="shadow-lg border border-gray-100 mb-6">
      <CardContent className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Mastery Progress Over Time</h3>
        <div style={{ height: '300px' }}>
          <Line data={chartData} options={options} />
        </div>
      </CardContent>
    </Card>
  );
};

// Mastery table component
interface MasteryTableProps {
  records: MasteryRecord[] | GradeMasteryRecord[];
  isLoading?: boolean;
  showGradeLevel?: boolean;
}

const MasteryTable: React.FC<MasteryTableProps> = ({
  records,
  isLoading = false,
  showGradeLevel = false
}) => {
  if (isLoading) {
    return (
      <Card className="shadow-lg border border-gray-100">
        <CardContent className="p-4">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-gray-300 rounded w-1/3 mb-6"></div>
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-300 rounded"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (records.length === 0) {
    return (
      <Card className="shadow-lg border border-gray-100">
        <CardContent className="p-6 text-center">
          <div className="text-gray-400 mb-4">📊</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Data Found</h3>
          <p className="text-gray-600">
            {showGradeLevel
              ? 'No students achieved any grade mastery in the selected date range.'
              : 'No students achieved mastery of all 4 L2 operations in the selected date range.'}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-lg border border-gray-100">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User Email
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Age Grade
                </th>
                {showGradeLevel && (
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Grade Level
                  </th>
                )}
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mastery Date
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {records.map((record, index) => {
                const key = showGradeLevel
                  ? `${record.userEmail}-${(record as GradeMasteryRecord).gradeLevel}-${record.masteryDate}`
                  : `${record.userEmail}-${record.masteryDate}`;

                return (
                  <tr key={key} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {record.userEmail}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {record.ageGrade !== null && record.ageGrade !== undefined ? record.ageGrade : 'N/A'}
                    </td>
                    {showGradeLevel && (
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {(record as GradeMasteryRecord).gradeLevel === 0 ? 'K' : (record as GradeMasteryRecord).gradeLevel}
                      </td>
                    )}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {new Date(record.masteryDate).toLocaleDateString('en-US', { timeZone: 'UTC' })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

// Error component
interface ErrorDisplayProps {
  error: string;
  onRetry: () => void;
}

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({ error, onRetry }) => (
  <Card className="shadow-lg border border-red-100">
    <CardContent className="p-6 text-center">
      <div className="text-red-400 mb-4">⚠️</div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Data</h3>
      <p className="text-gray-600 mb-4">{error}</p>
      <Button onClick={onRetry} className="bg-red-600 hover:bg-red-700 text-white">
        <FiRefreshCw className="w-4 h-4 mr-2" />
        Try Again
      </Button>
    </CardContent>
  </Card>
);

// Main component
export const GradeMasteryReport: React.FC = () => {
  const { user: currentUser } = useAuth();

  // Check if user is admin
  const isAdmin = useMemo(() =>
    currentUser?.role === 'admin',
    [currentUser?.role]
  );

  // Fixed date range: Aug 1, 2024 to today
  const startDate = '2024-08-01';
  const endDate = useMemo(() => formatDateLocal(new Date()), []);
  const dateRangeDisplay = useMemo(() => `August 1, 2024 - ${formatDateDisplay(new Date())}`, []);

  // State
  const [data, setData] = useState<GradeMasteryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'all' | 'any'>('all');

  // Calculate session counts for "All Grades" data
  const sessionCounts = useMemo(() => {
    if (!data || !data.allGradesMastered || data.allGradesMastered.length === 0) {
      return { session1: 0, session2: 0 };
    }

    // Session 1: School year start through Oct 3, 2025 (end of first evaluation period)
    // Session 2: Oct 4, 2025 onwards (second evaluation period)
    const session1Cutoff = new Date('2025-10-03T23:59:59Z');

    const session1 = data.allGradesMastered.filter(record => {
      const recordDate = new Date(record.masteryDate);
      return recordDate <= session1Cutoff;
    }).length;

    const session2 = data.allGradesMastered.filter(record => {
      const recordDate = new Date(record.masteryDate);
      return recordDate > session1Cutoff;
    }).length;

    return { session1, session2 };
  }, [data]);

  // Calculate chart data for both "All Grades" and "Any Grade" lines
  const chartData = useMemo(() => {
    if (!data) {
      return { allGrades: [], anyGrade: [] };
    }

    // Calculate "All Grades Mastered" data
    const allGradesData: Array<{ date: string; count: number }> = [];
    if (data.allGradesMastered && data.allGradesMastered.length > 0) {
      const dateCounts = new Map<string, number>();
      data.allGradesMastered.forEach(record => {
        const date = record.masteryDate;
        dateCounts.set(date, (dateCounts.get(date) || 0) + 1);
      });
      allGradesData.push(...Array.from(dateCounts.entries())
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()));
    }

    // Calculate "Any Grade Mastered" data (grade mastery events)
    const anyGradeData: Array<{ date: string; count: number }> = [];
    if (data.anyGradeMastered && data.anyGradeMastered.length > 0) {
      const dateCounts = new Map<string, number>();
      data.anyGradeMastered.forEach(record => {
        const date = record.masteryDate;
        dateCounts.set(date, (dateCounts.get(date) || 0) + 1);
      });
      anyGradeData.push(...Array.from(dateCounts.entries())
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()));
    }

    return { allGrades: allGradesData, anyGrade: anyGradeData };
  }, [data]);

  // Fetch data function
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await getGradeMasteryData(startDate, endDate);
      setData(result);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch mastery data';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate]);

  // Fetch data on mount
  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Retry function
  const handleRetry = useCallback(() => {
    fetchData();
  }, [fetchData]);

  // CSV download function
  const handleDownloadCSV = useCallback(() => {
    if (!data) return;

    const recordsToExport = activeTab === 'all' ? data.allGradesMastered : data.anyGradeMastered;
    if (!recordsToExport || recordsToExport.length === 0) return;

    // Define CSV headers based on active tab
    if (activeTab === 'all') {
      const headers = [
        { key: 'userEmail' as keyof MasteryRecord, label: 'User Email' },
        { key: 'ageGrade' as keyof MasteryRecord, label: 'Age Grade' },
        { key: 'masteryDate' as keyof MasteryRecord, label: 'Mastery Date' }
      ];

      const csvData = (recordsToExport as MasteryRecord[]).map(record => ({
        ...record,
        masteryDate: new Date(record.masteryDate).toLocaleDateString('en-US', { timeZone: 'UTC' }),
        ageGrade: record.ageGrade !== null && record.ageGrade !== undefined ? record.ageGrade : 'N/A'
      }));

      const csvContent = convertToCSV(csvData, headers);
      const filename = `grade-mastery-all-grades-${startDate}-to-${endDate}-${formatDateForFilename()}.csv`;
      downloadCSV(csvContent, filename);
    } else {
      const headers = [
        { key: 'userEmail' as keyof GradeMasteryRecord, label: 'User Email' },
        { key: 'ageGrade' as keyof GradeMasteryRecord, label: 'Age Grade' },
        { key: 'gradeLevel' as keyof GradeMasteryRecord, label: 'Grade Level' },
        { key: 'masteryDate' as keyof GradeMasteryRecord, label: 'Mastery Date' }
      ];

      const csvData = (recordsToExport as GradeMasteryRecord[]).map(record => ({
        ...record,
        masteryDate: new Date(record.masteryDate).toLocaleDateString('en-US', { timeZone: 'UTC' }),
        ageGrade: record.ageGrade !== null && record.ageGrade !== undefined ? record.ageGrade : 'N/A',
        gradeLevel: record.gradeLevel === 0 ? 'K' : record.gradeLevel.toString()
      }));

      const csvContent = convertToCSV(csvData, headers);
      const filename = `grade-mastery-any-grade-${startDate}-to-${endDate}-${formatDateForFilename()}.csv`;
      downloadCSV(csvContent, filename);
    }
  }, [data, startDate, endDate, activeTab]);

  // Early return if not admin
  if (!isAdmin) {
    return <AccessDenied />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white p-4">
      <div className="max-w-7xl mx-auto space-y-4">
        <MasteryReportHeader />

        {/* Navigation */}
        <div className="flex mb-4">
          <Link
            to="/admin"
            className="text-blue-600 hover:text-blue-800 transition-colors duration-300"
          >
            ← Back to Admin Dashboard
          </Link>
        </div>

        {/* Action Bar */}
        <ActionBar
          onRefresh={fetchData}
          onDownloadCSV={handleDownloadCSV}
          isLoading={isLoading}
          hasData={!!(data?.allGradesMastered && data.allGradesMastered.length > 0) || !!(data?.anyGradeMastered && data.anyGradeMastered.length > 0)}
          dateRange={dateRangeDisplay}
        />

        {/* Error Display */}
        {error && (
          <ErrorDisplay error={error} onRetry={handleRetry} />
        )}

        {/* Dashboard Content */}
        {!error && (
          <>
            {/* Summary Statistics */}
            <SummaryStats
              totalMasteries={data?.allGradesMastered?.length || 0}
              totalGradeMasteries={data?.anyGradeMastered?.length || 0}
              session1Count={sessionCounts.session1}
              session2Count={sessionCounts.session2}
              isLoading={isLoading}
            />

            {/* Chart with Both Lines */}
            <MasteryChart
              data={chartData}
              isLoading={isLoading}
            />

            {/* Tabs for Table Toggle */}
            <div className="mb-4">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                  <button
                    onClick={() => setActiveTab('all')}
                    className={`${
                      activeTab === 'all'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    All Grades Mastered ({data?.allGradesMastered?.length || 0})
                  </button>
                  <button
                    onClick={() => setActiveTab('any')}
                    className={`${
                      activeTab === 'any'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    Any Grade Mastered ({data?.anyGradeMastered?.length || 0})
                  </button>
                </nav>
              </div>
            </div>

            {/* Mastery Table */}
            {activeTab === 'all' && (
              <MasteryTable
                records={data?.allGradesMastered || []}
                isLoading={isLoading}
              />
            )}
            {activeTab === 'any' && (
              <MasteryTable
                records={data?.anyGradeMastered || []}
                isLoading={isLoading}
                showGradeLevel={true}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};
